import React, { useState, useEffect, useCallback } from 'react';
import { ethers } from 'ethers';

const GOV_ADDRESS = '0x9c36e515bd49AC146f3060e34eccC086007EECa6';

const GOV_ABI = [
  'function propose(string title, string description, uint8 proposalType, bytes callData) returns (uint256)',
  'function castVote(uint256 proposalId, uint8 choice)',
  'function queue(uint256 proposalId)',
  'function execute(uint256 proposalId)',
  'function cancel(uint256 proposalId)',
  'function proposalCount() view returns (uint256)',
  'function proposalState(uint256 proposalId) view returns (uint8)',
  'function getProposal(uint256 proposalId) view returns (tuple(uint256 id, address proposer, string title, string description, uint8 proposalType, bytes callData, uint256 createdBlock, uint256 startBlock, uint256 endBlock, uint256 executionBlock, uint256 forVotes, uint256 againstVotes, uint256 abstainVotes, bool executed, bool cancelled))',
  'function hasVoted(uint256, address) view returns (bool)',
  'function getVoterInfo(uint256 proposalId, address voter) view returns (bool voted, uint8 choice, uint256 weight)',
  'function stakedBalance(address) view returns (uint256)',
  'function votingWeight(address) view returns (uint256)',
  'function proposalDelay() view returns (uint256)',
  'function votingPeriod() view returns (uint256)',
  'function MIN_PROPOSAL_STAKE() view returns (uint256)',
];

const STATE_LABELS: Record<number, { label: string; color: string }> = {
  0: { label: 'Pending',   color: '#f59e0b' },
  1: { label: 'Active',    color: '#10b981' },
  2: { label: 'Succeeded', color: '#3b82f6' },
  3: { label: 'Defeated',  color: '#ef4444' },
  4: { label: 'Queued',    color: '#8b5cf6' },
  5: { label: 'Executed',  color: '#64748b' },
  6: { label: 'Cancelled', color: '#64748b' },
};

const TYPE_LABELS: Record<number, string> = {
  0: 'General',
  1: 'Update PI Params',
  2: 'Update Gov Params',
  3: 'Transfer Funds',
};

interface Proposal {
  id: bigint;
  proposer: string;
  title: string;
  description: string;
  proposalType: number;
  createdBlock: bigint;
  startBlock: bigint;
  endBlock: bigint;
  forVotes: bigint;
  againstVotes: bigint;
  abstainVotes: bigint;
  executed: boolean;
  cancelled: boolean;
  state: number;
}

interface Props {
  signer: ethers.JsonRpcSigner | null;
  provider: ethers.BrowserProvider | null;
  account: string;
}

export default function Proposals({ signer, provider, account }: Props) {
  const [proposals, setProposals]       = useState<Proposal[]>([]);
  const [loading, setLoading]           = useState(false);
  const [msg, setMsg]                   = useState('');
  const [showForm, setShowForm]         = useState(false);
  const [title, setTitle]               = useState('');
  const [description, setDescription]  = useState('');
  const [staked, setStaked]             = useState(BigInt(0));
  const [voteWeight, setVoteWeight]     = useState(BigInt(0));
  const [minStake, setMinStake]         = useState(BigInt(0));
  const [expandedId, setExpandedId]     = useState<bigint | null>(null);
  const [voterInfo, setVoterInfo]       = useState<Record<string, { voted: boolean; choice: number; weight: bigint }>>({});

  const loadProposals = useCallback(async () => {
    if (!provider || !account) return;
    try {
      const gov = new ethers.Contract(GOV_ADDRESS, GOV_ABI, provider);
      const count = await gov.proposalCount();
      const minS = await gov.MIN_PROPOSAL_STAKE();
      setMinStake(minS);

      const stk = await gov.stakedBalance(account);
      const vw  = await gov.votingWeight(account);
      setStaked(stk);
      setVoteWeight(vw);

      const list: Proposal[] = [];
      const vi: Record<string, { voted: boolean; choice: number; weight: bigint }> = {};

      for (let i = Number(count); i >= 1; i--) {
        const [p, state, info] = await Promise.all([
          gov.getProposal(i),
          gov.proposalState(i),
          gov.getVoterInfo(i, account),
        ]);
        list.push({
          id:            p.id,
          proposer:      p.proposer,
          title:         p.title,
          description:   p.description,
          proposalType:  Number(p.proposalType),
          createdBlock:  p.createdBlock,
          startBlock:    p.startBlock,
          endBlock:      p.endBlock,
          forVotes:      p.forVotes,
          againstVotes:  p.againstVotes,
          abstainVotes:  p.abstainVotes,
          executed:      p.executed,
          cancelled:     p.cancelled,
          state:         Number(state),
        });
        vi[i.toString()] = { voted: info[0], choice: Number(info[1]), weight: info[2] };
      }
      setProposals(list);
      setVoterInfo(vi);
    } catch (e) { console.error(e); }
  }, [provider, account]);

  useEffect(() => { loadProposals(); }, [loadProposals]);

  const handlePropose = async () => {
    if (!signer || !title || !description) return;
    setLoading(true); setMsg('');
    try {
      const gov = new ethers.Contract(GOV_ADDRESS, GOV_ABI, signer);
      const tx = await gov.propose(title, description, 0, '0x');
      setMsg('⏳ Submitting proposal...');
      await tx.wait();
      setMsg('✅ Proposal submitted!');
      setTitle(''); setDescription(''); setShowForm(false);
      await loadProposals();
    } catch (e: any) {
      setMsg(`❌ ${e.reason || e.message}`);
    }
    setLoading(false);
  };

  const handleVote = async (proposalId: bigint, choice: number) => {
    if (!signer) return;
    setLoading(true); setMsg('');
    try {
      const gov = new ethers.Contract(GOV_ADDRESS, GOV_ABI, signer);
      const tx = await gov.castVote(proposalId, choice);
      setMsg('⏳ Casting vote...');
      await tx.wait();
      setMsg('✅ Vote cast!');
      await loadProposals();
    } catch (e: any) {
      setMsg(`❌ ${e.reason || e.message}`);
    }
    setLoading(false);
  };

  const handleQueue = async (proposalId: bigint) => {
    if (!signer) return;
    setLoading(true); setMsg('');
    try {
      const gov = new ethers.Contract(GOV_ADDRESS, GOV_ABI, signer);
      const tx = await gov.queue(proposalId);
      await tx.wait();
      setMsg('✅ Proposal queued!');
      await loadProposals();
    } catch (e: any) { setMsg(`❌ ${e.reason || e.message}`); }
    setLoading(false);
  };

  const handleExecute = async (proposalId: bigint) => {
    if (!signer) return;
    setLoading(true); setMsg('');
    try {
      const gov = new ethers.Contract(GOV_ADDRESS, GOV_ABI, signer);
      const tx = await gov.execute(proposalId);
      await tx.wait();
      setMsg('✅ Proposal executed!');
      await loadProposals();
    } catch (e: any) { setMsg(`❌ ${e.reason || e.message}`); }
    setLoading(false);
  };

  const shortAddr = (addr: string) => `${addr.slice(0, 6)}...${addr.slice(-4)}`;
  const totalVotes = (p: Proposal) => p.forVotes + p.againstVotes + p.abstainVotes;
  const forPct = (p: Proposal) => {
    const t = totalVotes(p);
    return t > BigInt(0) ? Number((p.forVotes * BigInt(100)) / t) : 0;
  };

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 24px 60px' }}>
      {/* ── Header ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h2 style={{ fontSize: 22, fontWeight: 800, color: '#fff', margin: 0 }}>🗳️ Governance Proposals</h2>
          <p style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>
            Staked: <b style={{ color: '#f7931a' }}>{parseFloat(ethers.formatEther(staked)).toLocaleString()} TBC</b>
            {' · '}Voting weight: <b style={{ color: '#10b981' }}>{voteWeight.toString()} votes</b>
          </p>
        </div>
        {account && (
          <button
            className="action-btn primary"
            onClick={() => setShowForm(!showForm)}
            disabled={staked < minStake}
            title={staked < minStake ? `Need ${ethers.formatEther(minStake)} TBC staked to propose` : ''}
          >
            {showForm ? '✕ Cancel' : '+ New Proposal'}
          </button>
        )}
      </div>

      {/* ── New Proposal Form ── */}
      {showForm && (
        <div className="panel" style={{ marginBottom: 24 }}>
          <h3>📝 Submit New Proposal</h3>
          <input
            className="tbc-input"
            placeholder="Proposal title..."
            value={title}
            onChange={e => setTitle(e.target.value)}
            style={{ width: '100%', marginBottom: 12 }}
          />
          <textarea
            className="tbc-input"
            placeholder="Full description / rationale..."
            value={description}
            onChange={e => setDescription(e.target.value)}
            rows={4}
            style={{ width: '100%', marginBottom: 12, resize: 'vertical' }}
          />
          <button className="action-btn primary" onClick={handlePropose} disabled={loading || !title || !description}>
            Submit Proposal
          </button>
          {msg && <div className={msg.startsWith('✅') ? 'success-msg' : msg.startsWith('❌') ? 'error-msg' : 'loading'} style={{ marginTop: 10 }}>{msg}</div>}
        </div>
      )}

      {/* ── Proposal List ── */}
      {proposals.length === 0 ? (
        <div className="panel" style={{ textAlign: 'center', padding: '40px 20px', color: '#475569' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
          <p>No proposals yet. Stake 1,000+ TBC to submit the first one!</p>
        </div>
      ) : (
        proposals.map(p => {
          const stateInfo = STATE_LABELS[p.state] || { label: 'Unknown', color: '#64748b' };
          const vi = voterInfo[p.id.toString()];
          const fp = forPct(p);
          const isExpanded = expandedId === p.id;

          return (
            <div key={p.id.toString()} className="panel" style={{ marginBottom: 16, cursor: 'pointer' }} onClick={() => setExpandedId(isExpanded ? null : p.id)}>
              {/* ── Card Header ── */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                    <span style={{ fontSize: 12, color: '#64748b' }}>#{p.id.toString()}</span>
                    <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 6, background: `${stateInfo.color}22`, color: stateInfo.color, fontWeight: 700 }}>
                      {stateInfo.label}
                    </span>
                    <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 6, background: 'rgba(255,255,255,0.05)', color: '#94a3b8' }}>
                      {TYPE_LABELS[p.proposalType]}
                    </span>
                    {vi?.voted && (
                      <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 6, background: 'rgba(16,185,129,0.1)', color: '#10b981' }}>
                        ✓ Voted {vi.choice === 1 ? 'For' : vi.choice === 0 ? 'Against' : 'Abstain'}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: '#fff' }}>{p.title}</div>
                  <div style={{ fontSize: 12, color: '#475569', marginTop: 4 }}>by {shortAddr(p.proposer)}</div>
                </div>
                <div style={{ fontSize: 20, color: '#475569', marginLeft: 12 }}>{isExpanded ? '▲' : '▼'}</div>
              </div>

              {/* ── Vote Bar ── */}
              <div style={{ marginTop: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#64748b', marginBottom: 6 }}>
                  <span>For: <b style={{ color: '#10b981' }}>{p.forVotes.toString()}</b></span>
                  <span>Against: <b style={{ color: '#ef4444' }}>{p.againstVotes.toString()}</b></span>
                  <span>Abstain: <b style={{ color: '#94a3b8' }}>{p.abstainVotes.toString()}</b></span>
                </div>
                <div style={{ height: 6, background: 'rgba(255,255,255,0.07)', borderRadius: 999, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${fp}%`, background: 'linear-gradient(90deg, #10b981, #34d399)', borderRadius: 999, transition: 'width 0.5s' }} />
                </div>
              </div>

              {/* ── Expanded Details ── */}
              {isExpanded && (
                <div style={{ marginTop: 20, paddingTop: 20, borderTop: '1px solid rgba(255,255,255,0.07)' }} onClick={e => e.stopPropagation()}>
                  <p style={{ fontSize: 14, color: '#94a3b8', lineHeight: 1.7, marginBottom: 20 }}>{p.description}</p>

                  <div style={{ display: 'flex', gap: 16, fontSize: 12, color: '#475569', marginBottom: 20 }}>
                    <span>Created: block #{p.createdBlock.toString()}</span>
                    <span>Voting: #{p.startBlock.toString()} → #{p.endBlock.toString()}</span>
                  </div>

                  {/* ── Vote Buttons ── */}
                  {p.state === 1 && !vi?.voted && account && (
                    <div style={{ display: 'flex', gap: 10 }}>
                      <button className="action-btn" style={{ background: 'rgba(16,185,129,0.15)', color: '#10b981', border: '1px solid rgba(16,185,129,0.3)' }}
                        onClick={() => handleVote(p.id, 1)} disabled={loading}>
                        ✅ Vote For
                      </button>
                      <button className="action-btn" style={{ background: 'rgba(239,68,68,0.15)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.3)' }}
                        onClick={() => handleVote(p.id, 0)} disabled={loading}>
                        ❌ Vote Against
                      </button>
                      <button className="action-btn" style={{ background: 'rgba(148,163,184,0.1)', color: '#94a3b8', border: '1px solid rgba(148,163,184,0.2)' }}
                        onClick={() => handleVote(p.id, 2)} disabled={loading}>
                        ➖ Abstain
                      </button>
                    </div>
                  )}

                  {/* ── Queue / Execute ── */}
                  {p.state === 2 && (
                    <button className="action-btn primary" onClick={() => handleQueue(p.id)} disabled={loading}>
                      📥 Queue for Execution
                    </button>
                  )}
                  {p.state === 4 && (
                    <button className="action-btn primary" onClick={() => handleExecute(p.id)} disabled={loading}>
                      ⚡ Execute Proposal
                    </button>
                  )}

                  {msg && <div className={msg.startsWith('✅') ? 'success-msg' : msg.startsWith('❌') ? 'error-msg' : 'loading'} style={{ marginTop: 10 }}>{msg}</div>}
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}
